import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { decodeData, encodeData } from "../../../utils/encodeAndEncode";
import { getCourseModule, createModule, updateModule, deleteModule, moduleStatus } from "../../../services/adminService";
import Loader from '../../../components/admin/Loader';
import AdminPage from '../../../components/admin/AdminPage';
import { toast } from 'react-toastify';
import NoData from "../../../components/common/NoData";
import { usePermissions } from '../../../context/PermissionsContext';

function  CourseModule() {
  const { courseId } = useParams();
  const decodedCourseId = decodeData(courseId);
  const { permissions } = usePermissions();
  
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModuleId, setDeleteModuleId] = useState(null);
  const [editModuleId, setEditModuleId] = useState(null);
  const [newModuleName, setNewModuleName] = useState("");
  const [newModuleDescription, setNewModuleDescription] = useState("");
  const [moduleData, setModuleData] = useState([]);
  const [statusLoading, setStatusLoading] = useState(null);
  const [classroomName, setClassroomName] = useState(null);
  
  const [errors, setErrors] = useState({
    moduleName: "",
    moduleDescription: ""
  });

  const fetchCourseModule = async (searchQuery = "") => {
    try {
      if (!searchQuery) {
        setLoading(true); // Only show loading on initial fetch, not during search
      }
      const response = await getCourseModule({ 
        id: decodedCourseId,
        search: searchQuery 
      });
      setClassroomName(response.course_name);
      // console.log('classroomName---------------', classroomName);
      // console.log('get module response', response);
      
      if (response.success) {
        
        const formattedModules = response.data.modulesWithDetails.map(module => ({
          id: module.id,
          title: module.module_name,
          subtitle: module.module_desc,
          status: module.is_active ? "Active" : "Deactive",
          videos: module.video_counts,
          assessments: module.assessment_counts,
          surveys: module.survey_counts,
          documents: module.document_counts,
          assignments: module.assignment_counts,
          totalContent: module.video_counts + module.assessment_counts + 
                       module.survey_counts + module.assignment_counts + 
                       module.document_counts
        }));
        setModuleData(formattedModules);
      } else {
        setModuleData([]);
      }
    } catch (error) {
      // console.log('error', error);
      setModuleData([]);
      toast.error('Error fetching modules: ' + error.message, {
        position: 'top-center',
        autoClose: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourseModule();
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchCourseModule(search);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [search]);

  // Reset errors when modal is opened/closed
  useEffect(() => {
    if (showModal) {
      setErrors({
        moduleName: "",
        moduleDescription: ""
      });
    }
  }, [showModal]);

  const validateForm = () => {
    const newErrors = {
      moduleName: "",
      moduleDescription: ""
    };
    let isValid = true;

    if (!newModuleName.trim()) {
      newErrors.moduleName = "Module Name is required";
      isValid = false;
    } else if (newModuleName.trim().length > 30) {
      newErrors.moduleName = "Module Name cannot exceed 30 characters";
      isValid = false;
    }

    if (!newModuleDescription.trim()) {
      newErrors.moduleDescription = "Module Description is required";
      isValid = false;
    } else if (newModuleDescription.trim().length > 50) {
      newErrors.moduleDescription = "Module Description cannot exceed 50 characters";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSaveModule = async () => {
    if (editModuleId && !permissions.course_module_management_edit) {
      toast.warning("You don't have permission to edit modules");
      return;
    }
    if (!editModuleId && !permissions.course_module_management_create) {
      toast.warning("You don't have permission to create modules");
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      let response;

      if (editModuleId) {
        response = await updateModule(editModuleId, {
          module_name: newModuleName.trim(),
          module_desc: newModuleDescription.trim()
        });
      } else {
        response = await createModule({
          course_id: decodedCourseId,
          module_name: newModuleName.trim(),
          module_desc: newModuleDescription.trim(),
          module_sequence: moduleData.length + 1
        });
      }

      if (response.success) {
        toast.success(editModuleId ? 'Module updated successfully' : 'Module created successfully', {
          position: 'top-center',
          autoClose: 3000
        });
        setShowModal(false);
        fetchCourseModule(search);
      } else {
        toast.error(response.message || 'Operation failed', {
          position: 'top-center',
          autoClose: 3000
        });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Operation failed: ' + error.message, {
        position: 'top-center',
        autoClose: 3000
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModule = async () => {
    if (!permissions.course_module_management_delete) {
      toast.warning("You don't have permission to delete modules");
      return;
    }

    try {
      setSaving(true);
      const response = await deleteModule({
        course_id: decodedCourseId,
        module_id: deleteModuleId
      });

      if (response.success) {
        toast.success('Module deleted successfully', {
          position: 'top-center',
          autoClose: 3000
        });
        setShowDeleteModal(false);
        setDeleteModuleId(null);
        fetchCourseModule(search); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to delete module', {
          position: 'top-center',
          autoClose: 3000
        });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete module: ' + error.message, {
        position: 'top-center',
        autoClose: 3000
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEditModule = (module) => {
    if (!permissions.course_module_management_edit) {
      toast.warning("You don't have permission to edit modules");
      return;
    }
    setEditModuleId(module.id);
    setNewModuleName(module.title);
    setNewModuleDescription(module.subtitle);
    setShowModal(true);
  };

  const handleToggleStatus = async (e, module) => {
    e.stopPropagation();
    if (!permissions.course_module_management_status) {
      toast.warning("You don't have permission to change module status");
      return;
    }

    try {
      setStatusLoading(module.id);
      const newStatus = module.status === "Active" ? 0 : 1; // Convert status to number (0 for inactive, 1 for active)
      
      const response = await moduleStatus({
        course_id: decodedCourseId,
        module_id: module.id,
        status: newStatus
      });

      if (response.success) {
        toast.success(`Module ${newStatus ? 'activated' : 'deactivated'} successfully`, {
          position: 'top-center',
          autoClose: 3000
        });
        fetchCourseModule(search); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to update module status', {
          position: 'top-center',
          autoClose: 3000
        });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to update module status: ' + error.message, {
        position: 'top-center',
        autoClose: 3000
      });
    } finally {
      setStatusLoading(null);
    }
  };

  const getStatusClass = (status) => {
    const statusClasses = {
      Active: "badge bg-success-subtle border border-success text-success p-2",
      Deactive: "badge bg-warning-subtle border border-warning text-warning p-2",
      Pending: "badge bg-info-subtle border border-info text-info p-2",
    };
    return statusClasses[status] || "badge bg-secondary-subtle border border-secondary p-2";
  };

  const HandleRedirectToModuleContent = (moduleId) => {
    if (!permissions.course_module_content_management_view) {
      toast.warning("You don't have permission to view module content");
      return;
    }
    
    // console.log('decodedCourseId-------', decodedCourseId);
    // console.log('moduleId-------', moduleId);
    
    const encodedCourseId = encodeData(decodedCourseId);
    const encodedModuleId = encodeData(moduleId);

    // console.log('encodedCourseId', encodedCourseId);
    // console.log('encodedModuleId', encodedModuleId);

    navigate(`/admin/courses/module/${encodedCourseId}/content/${encodedModuleId}`);
  };

  return (
    <AdminPage
      back={
        <button
          type="button"
          onClick={() => navigate('/admin/courses')}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50"
        >
          <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
          Back
        </button>
      }
      title={classroomName ? `${classroomName} · Course Modules` : 'Course Modules'}
      description={classroomName ? `Manage modules for: ${classroomName}` : 'Add and manage course modules and content.'}
      actions={
        <>
          <button
            type="button"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => {
              if (!permissions.course_module_management_create) {
                toast.warning("You don't have permission to create modules");
                return;
              }
              setEditModuleId(null);
              setNewModuleName("");
              setNewModuleDescription("");
              setShowModal(true);
            }}
            disabled={!permissions.course_module_management_create}
            title={!permissions.course_module_management_create ? "You don't have permission to create modules" : "Add Module"}
          >
            <Icon icon="fluent:add-24-regular" width="18" height="18" />
            Add Module
          </button>
        </>
      }
    >
      {loading ? (
        <div className="flex justify-center items-center min-h-[320px] rounded-2xl bg-gray-50/50 border border-gray-100">
          <Loader />
        </div>
      ) : (
        <>
          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-sm">
              <Icon icon="fluent:search-20-regular" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-[#4F6BFF]/20 focus:border-[#4F6BFF] outline-none transition-all text-sm"
                placeholder="Search modules..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Module Cards */}
          <div className="space-y-3">
                {moduleData.length === 0 ? (
                  <div className="rounded-xl border border-gray-100 bg-gray-50/50 py-12">
                    <NoData 
                      message={search ? "No modules found for your search" : "No modules found. Click 'Add Module' to create one."} 
                      caption="No modules found"
                    />
                  </div>
                ) : (
                  moduleData.map((module) => (
                    <div key={module.id} className="w-full">
                      <div 
                        className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md hover:border-[#4F6BFF] transition-all group cursor-pointer" 
                        onClick={() => HandleRedirectToModuleContent(module.id)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1.5">
                              <h5 className="mb-0 text-base font-semibold text-gray-900 truncate">{module.title}</h5>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium shrink-0 ${module.status === "Active" ? "bg-green-50 text-green-700 border border-green-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                                {module.status}
                              </span>
                            </div>
                            <p className="text-gray-600 text-sm mb-3 line-clamp-2">{module.subtitle}</p>
                            
                            <div className="flex flex-wrap gap-2">
                              {module.videos > 0 && (
                                <div className="flex items-center gap-1 bg-blue-50 text-blue-700 rounded-lg px-2 py-1 text-xs font-medium">
                                  <Icon icon="fluent:video-24-filled" width="14" height="14" />
                                  <span>{module.videos}</span>
                                </div>
                              )}
                              {module.assessments > 0 && (
                                <div className="flex items-center gap-1 bg-purple-50 text-purple-700 rounded-lg px-2 py-1 text-xs font-medium">
                                  <Icon icon="fluent:clipboard-task-24-filled" width="14" height="14" />
                                  <span>{module.assessments}</span>
                                </div>
                              )}
                              {module.surveys > 0 && (
                                <div className="flex items-center gap-1 bg-pink-50 text-pink-700 rounded-lg px-2 py-1 text-xs font-medium">
                                  <Icon icon="fluent:poll-24-filled" width="14" height="14" />
                                  <span>{module.surveys}</span>
                                </div>
                              )}
                              {module.documents > 0 && (
                                <div className="flex items-center gap-1 bg-orange-50 text-orange-700 rounded-lg px-2 py-1 text-xs font-medium">
                                  <Icon icon="fluent:document-24-filled" width="14" height="14" />
                                  <span>{module.documents}</span>
                                </div>
                              )}
                              {module.assignments > 0 && (
                                <div className="flex items-center gap-1 bg-green-50 text-green-700 rounded-lg px-2 py-1 text-xs font-medium">
                                  <Icon icon="fluent:task-list-24-filled" width="14" height="14" />
                                  <span>{module.assignments}</span>
                                </div>
                              )}
                              {module.totalContent === 0 && (
                                <span className="text-xs text-gray-400 italic">No content added yet</span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex shrink-0 gap-1.5" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className="border border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400 font-medium rounded-lg text-sm inline-flex items-center justify-center w-8 h-8 transition-colors"
                              title={!permissions.course_module_management_edit ? "You don't have permission to edit modules" : "Edit Module"}
                              onClick={(e) => { e.stopPropagation(); handleEditModule(module); }}
                              disabled={!permissions.course_module_management_edit}
                            >
                              <Icon icon="fluent:edit-24-regular" width="16" height="16" />
                            </button>
                            <button
                              type="button"
                              className="border border-gray-300 text-gray-600 hover:bg-red-50 hover:text-red-600 hover:border-red-300 font-medium rounded-lg text-sm inline-flex items-center justify-center w-8 h-8 transition-colors"
                              title={!permissions.course_module_management_delete ? "You don't have permission to delete modules" : "Delete Module"}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!permissions.course_module_management_delete) { toast.warning("You don't have permission to delete modules"); return; }
                                setDeleteModuleId(module.id);
                                setShowDeleteModal(true);
                              }}
                              disabled={!permissions.course_module_management_delete}
                            >
                              <Icon icon="fluent:delete-24-regular" width="16" height="16" />
                            </button>
                            <button
                              type="button"
                              className={`font-medium rounded-lg text-sm inline-flex items-center justify-center w-8 h-8 transition-colors ${module.status === "Active" ? "bg-green-500 hover:bg-green-600 text-white" : "border border-amber-500 text-amber-600 hover:bg-amber-50"}`}
                              title={!permissions.course_module_management_status ? "You don't have permission to change status" : `${module.status === "Active" ? "Deactivate" : "Activate"} Module`}
                              onClick={(e) => handleToggleStatus(e, module)}
                              disabled={statusLoading === module.id || !permissions.course_module_management_status}
                            >
                              {statusLoading === module.id ? (
                                <span className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-current border-t-transparent" />
                              ) : (
                                <Icon icon={module.status === "Active" ? "fluent:checkmark-circle-24-filled" : "fluent:dismiss-circle-24-regular"} width="16" height="16" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
          </div>

          {/* Add/Edit Module Modal */}
          {showModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                  <h5 className="text-lg font-semibold">{editModuleId ? "Edit Module" : "Add New Module"}</h5>
                  <button type="button" className="text-gray-400 hover:text-gray-600" onClick={() => setShowModal(false)}>
                    <Icon icon="mdi:close" width="24" height="24" />
                  </button>
                </div>
                <div className="p-6">
                  {saving ? (
                    <div className="text-center py-4">
                      <Loader />
                    </div>
                  ) : (
                    <>
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-font-primary mb-1.5">Module Name *</label>
                        <input
                          type="text"
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${errors.moduleName ? 'border-red-500' : 'border-input-border'}`}
                          value={newModuleName}
                          onChange={(e) => {
                            setNewModuleName(e.target.value);
                            if (errors.moduleName) {
                              setErrors({...errors, moduleName: ""});
                            }
                          }}
                          placeholder="Enter module name"
                          maxLength={75}
                        />
                        {errors.moduleName ? (
                          <div className="text-red-500 text-sm mt-1">{errors.moduleName}</div>
                        ) : (
                          <small className="text-gray-500">Maximum 75 characters allowed ({75 - newModuleName.length} remaining)</small>
                        )}
                      </div>
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-font-primary mb-1.5">Module Description *</label>
                        <textarea
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${errors.moduleDescription ? 'border-red-500' : 'border-input-border'}`}
                          rows="3"
                          value={newModuleDescription}
                          onChange={(e) => {
                            setNewModuleDescription(e.target.value);
                            if (errors.moduleDescription) {
                              setErrors({...errors, moduleDescription: ""});
                            }
                          }}
                          placeholder="Enter description"
                          maxLength={150}
                        ></textarea>
                        {errors.moduleDescription ? (
                          <div className="text-red-500 text-sm mt-1">{errors.moduleDescription}</div>
                        ) : (
                          <small className="text-gray-500">Maximum 150 characters allowed ({150 - newModuleDescription.length} remaining)</small>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
                  <button 
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg"
                    onClick={() => setShowModal(false)}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg"
                    onClick={handleSaveModule}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      editModuleId ? "Update Module" : "Save Module"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                  <h5 className="text-lg font-semibold text-red-500">Confirm Deletion</h5>
                  <button type="button" className="text-gray-400 hover:text-gray-600" onClick={() => setShowDeleteModal(false)}>
                    <Icon icon="mdi:close" width="24" height="24" />
                  </button>
                </div>
                <div className="p-6">
                  {saving ? (
                    <div className="text-center py-4">
                      <Loader />
                    </div>
                  ) : (
                    <p>Are you sure you want to delete this module?</p>
                  )}
                </div>
                <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
                  <button 
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg"
                    onClick={() => setShowDeleteModal(false)}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button 
                    className="bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg"
                    onClick={handleDeleteModule}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block" role="status" aria-hidden="true"></span>
                        Deleting...
                      </>
                    ) : (
                      "Confirm Delete"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </AdminPage>
  );
}

export default CourseModule;
